import { ApiError } from '@bbr/api-client';
import { IconMoodSadDizzy } from '@bbr/icons/mood-sad-dizzy-outline';
import { useIntl } from '@bbr/intl';
import { Button, Heading } from '@bbr/ui';
import { defineMessage } from '@formatjs/intl';
import { HttpStatusCode } from '@solidjs/start';
import { Match, Show, ErrorBoundary as SolidErrorBoundary, Switch } from 'solid-js';
import { getConfigValue } from '../modules/config.ts';
import type { JSXElement } from 'solid-js';

type Props = {
	fallback?: ((err: unknown, reset: () => void) => JSXElement) | JSXElement;
	children: JSXElement;
};

export function ErrorBoundary(props: Props) {
	return (
		<SolidErrorBoundary
			fallback={(err, reset) => (
				<>
					{typeof props.fallback === 'function'
						? props.fallback(err, reset)
						: props.fallback || <ErrorBoundaryFallback err={err as Error} reset={reset} />}
				</>
			)}
		>
			{props.children}
		</SolidErrorBoundary>
	);
}

function ErrorBoundaryFallback(props: { err: Error | ApiError; reset: () => void }) {
	const intl = useIntl();
	return (
		<Switch>
			<Match when={props.err instanceof ApiError && props.err.code === 'trail.not-found'}>
				<div class="flex flex-col items-center justify-center">
					<HttpStatusCode code={404} />
					<Heading level="h1" class="text-8xl">
						404
					</Heading>
					<p>{intl().t(notFound)}</p>
				</div>
			</Match>
			<Match when>
				<section class="mx-auto grid size-full max-w-xl grow place-items-center">
					<div class="flex flex-col gap-8 overflow-hidden">
						<Heading level="h1" class="text-yellow-600 dark:text-yellow-400">
							<IconMoodSadDizzy /> {intl().t(errorTitle)}
						</Heading>

						<Show when={getConfigValue('ENVIRONMENT') === 'development'}>
							<pre class="max-h-64 max-w-8xl overflow-auto rounded-xl bg-zinc-950 p-4 whitespace-pre text-white">
								{props.err.toString()}
								{props.err.stack}
							</pre>
						</Show>

						<Button onClick={props.reset}>{intl().t(tryAgain)}</Button>
					</div>
				</section>
			</Match>
		</Switch>
	);
}

const errorTitle = defineMessage({ defaultMessage: 'An error occurred' });
const tryAgain = defineMessage({ defaultMessage: 'Try again' });

const notFound = defineMessage({ defaultMessage: 'Trail not found' });
